import { Button, IconMedallion } from '@ai-tutor/ui';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useEntitlementsUsage } from '@/hooks/account/useBilling';
import { track } from '@/config/observability';
import { describeInlineLimit, tierOf } from '@/lib/budget-limit';

/**
 * Inline paywall for surfaces where a generation failed because of
 * budget exhaustion (a lesson step, etc). Same copy rules as
 * `BudgetExceededDialog` (`lib/budget-limit.ts`) but rendered in-place rather
 * than as an overlay - appropriate when the user navigated to a specific
 * artifact that didn't generate.
 *
 * The failed step's error carries no reason, window or reset, so `feature`
 * (the step's usage key) is looked up in `/entitlements/usage` - the same
 * query the root-mounted dialog keeps warm, so this adds no request. When that
 * feature is exhausted the card names the window ("all 10 of this month's
 * lessons"); otherwise it names none. Free gets "Upgrade to Plus", Plus no
 * button, an unknown tier "See plans".
 */
export function BudgetExceededCard({
  feature,
  title = "We couldn't finish this generation",
  onUpgrade,
}: {
  /** Backend feature key of the step that failed (`text`, `podcast`, ...). */
  feature?: string;
  title?: string;
  onUpgrade?: () => void;
}) {
  const navigate = useNavigate();
  const usage = useEntitlementsUsage();
  const copy = describeInlineLimit({ feature, usage: usage.data });
  // Once per card shown, after the tier is known.
  const tier = usage.data ? tierOf(usage.data) : null;
  useEffect(() => {
    if (!tier) return;
    track('paywall_shown', {
      surface: 'inline_card',
      reason: 'step_limit',
      feature: feature ?? 'unknown',
      tier,
    });
  }, [feature, tier]);
  const handleUpgrade = () => {
    track('upgrade_clicked', { surface: 'inline_card', feature });
    if (onUpgrade) onUpgrade();
    else navigate('/settings?tab=payment');
  };

  return (
    <div className='rounded-lg border border-warning/40 bg-warning/5 p-6 dark:border-warning/30 dark:bg-warning/10'>
      <div className='flex items-start gap-3'>
        <IconMedallion tone='warning' size='sm' className='h-10 w-10'>
          <Sparkles className='h-5 w-5' />
        </IconMedallion>
        <div className='flex-1'>
          <h3 className='font-semibold text-foreground'>{title}</h3>
          <p className='mt-1 text-sm text-muted-foreground'>{copy.body}</p>
          {copy.cta !== 'none' && (
            <div className='mt-4'>
              <Button size='sm' onClick={handleUpgrade} className='tap-target'>
                {copy.cta === 'upgrade' ? 'Upgrade to Plus' : 'See plans'}
                <ArrowRight className='ml-1 h-3.5 w-3.5' />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
